import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/auth'

const API_BASE_URL = '/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const { refreshToken, setTokens, logout } = useAuthStore.getState()

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token } = response.data
          setTokens(access_token, refresh_token)

          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          // Only logout on auth rejection (4xx), not on network/server errors
          const status = (refreshError as AxiosError)?.response?.status
          if (status && status >= 400 && status < 500) {
            logout()
          }
        }
      } else {
        logout()
      }
    }

    return Promise.reject(error)
  }
)

// API Types
export interface ApiError {
  error: {
    code: string
    message: string
    details: Record<string, unknown>
  }
}

// Auth API
export const authApi = {
  requestOTP: (phone_number: string) =>
    api.post<{ message: string; phone_number: string; debug_otp?: string }>('/auth/otp/request', {
      phone_number,
    }),

  verifyOTP: (phone_number: string, otp: string) =>
    api.post<{
      access_token: string
      refresh_token: string
      user: Record<string, unknown>
      is_new_user: boolean
    }>('/auth/otp/verify', { phone_number, otp }),

  requestEmailOTP: (email: string) =>
    api.post<{ message: string; email: string; debug_otp?: string }>('/auth/otp/email/request', {
      email,
    }),

  verifyEmailOTP: (email: string, otp: string) =>
    api.post<{
      access_token: string
      refresh_token: string
      user: Record<string, unknown>
      is_new_user: boolean
    }>('/auth/otp/email/verify', { email, otp }),

  logout: (refresh_token: string) =>
    api.post('/auth/logout', { refresh_token }),
}

// User API
export const userApi = {
  getMe: () => api.get('/me'),
  updateMe: (data: { name?: string; situations_selected?: string[] }) =>
    api.put('/me', data),

  getChildProfile: () => api.get('/child-profile'),
  createChildProfile: (data: { name: string; date_of_birth: string }) =>
    api.post('/child-profile', data),
  updateChildProfile: (data: { name?: string; date_of_birth?: string }) =>
    api.put('/child-profile', data),
  updateSensoryProfile: (data: { onboarding_answers: Record<string, string>; situations_selected?: string[] }) =>
    api.put('/child-profile/sensory', data),
  deleteAccount: () => api.delete('/me/delete'),
}

// Subscription API types
export type AccessLevel = 'trial' | 'locked' | 'premium'

export interface SubscriptionStatus {
  id: string
  status: 'none' | 'trial' | 'active' | 'past_due' | 'cancelled' | 'expired'
  is_premium: boolean
  is_paid?: boolean
  access_level?: AccessLevel
  trial_started_at?: string
  trial_ends_at?: string
  trial_days_remaining?: number
  current_period_start?: string
  current_period_end?: string
  cancelled_at?: string
  cancel_at_period_end?: boolean
}

export type ContentType = 'activity' | 'printable'

export interface ContentCredits {
  limit: number | null
  used: number
  remaining: number | null
  unlimited: boolean
}

export interface ContentAccess {
  access_level: AccessLevel
  is_premium: boolean
  is_paid: boolean
  trial_ends_at?: string
  trial_days_remaining: number
  programs_locked: boolean
  credits: Record<ContentType, ContentCredits>
  unlocked: Record<ContentType, string[]>
}

export interface VerifyStoreReceiptRequest {
  platform: 'apple' | 'google'
  receipt_token: string
  product_id: string
}

// Subscription API
export const subscriptionApi = {
  getStatus: () => api.get<SubscriptionStatus>('/subscription'),

  verifyStoreReceipt: (data: VerifyStoreReceiptRequest) =>
    api.post<{ success: boolean; message: string; subscription: SubscriptionStatus }>(
      '/subscription/verify-store',
      data
    ),

  // Re-verify with the store on app launch so a renewal/cancel/expiry is
  // reflected without waiting for the daily reconcile job.
  refresh: () =>
    api.post<{ success: boolean; subscription: SubscriptionStatus }>(
      '/subscription/refresh',
      {}
    ),
}

// Content gating API (hard-paywall trial model — server is source of truth)
export const contentApi = {
  getAccess: () => api.get<ContentAccess>('/content/access'),
  unlock: (type: ContentType, id: string) =>
    api.post<ContentAccess>('/content/unlock', { type, id }),
}

// Activity API
export const activityApi = {
  getLogs: (params?: { activity_id?: string; situation_id?: string }) =>
    api.get('/activity-logs', { params }),
  createLog: (data: { activity_id: string; situation_id?: string }) =>
    api.post('/activity-logs', data),
  updateLog: (id: string, data: { feedback?: string; completed?: boolean }) =>
    api.put(`/activity-logs/${id}`, data),

  getSaved: () => api.get('/saved-activities'),
  saveActivity: (activity_id: string, note?: string) =>
    api.post('/saved-activities', { activity_id, note }),
  unsaveActivity: (id: string) => api.delete(`/saved-activities/${id}`),
}

// Reminder types
export interface RepeatConfig {
  type: 'once' | 'daily' | 'weekly' | 'custom'
  days?: number[]
  end_date?: string
}

export interface Reminder {
  id: string
  activity_id: string
  activity_title: string
  child_profile: string | null
  scheduled_at: string
  repeat_config: RepeatConfig
  status: 'pending' | 'triggered' | 'completed' | 'missed' | 'cancelled'
  is_recurring: boolean
  repeat_type: string
  last_triggered_at: string | null
  trigger_count: number
  created_at: string
  updated_at: string
}

// Reminder API
export const reminderApi = {
  getReminders: (params?: { status?: string; activity_id?: string; all?: boolean }) =>
    api.get<Reminder[]>('/reminders', { params }),

  createReminder: (data: {
    activity_id: string
    activity_title?: string
    scheduled_at: string
    repeat_config?: RepeatConfig
    child_profile?: string
  }) => api.post<Reminder>('/reminders', data),

  updateReminder: (id: string, data: {
    scheduled_at?: string
    repeat_config?: RepeatConfig
  }) => api.put<Reminder>(`/reminders/${id}`, data),

  deleteReminder: (id: string) => api.delete(`/reminders/${id}`),

  dismissReminder: (id: string) => api.post<Reminder>(`/reminders/${id}/dismiss`),

  getMissedReminders: () => api.get<Reminder[]>('/reminders/missed'),
}

// Crisis API
export interface CrisisLogCreate {
  situation_id?: string
  intensity: number
  technique_used?: string
  what_helped?: string
  duration_seconds: number
}

export interface CrisisLogEntry {
  id: string
  situation_id: string
  intensity: number
  technique_used: string
  what_helped: string
  duration_seconds: number
  created_at: string
}

export const crisisApi = {
  getLogs: () => api.get<CrisisLogEntry[]>('/crisis-logs'),
  createLog: (data: CrisisLogCreate) => api.post<CrisisLogEntry>('/crisis-logs', data),
}

// Progress API
export interface ProgressSummary {
  total: number
  this_week: number
  current_streak: number
  longest_streak: number
}

export interface WeeklyData {
  day: string
  date: string
  count: number
}

export interface CalendarDay {
  date: string
  count: number
}

export interface CalendarData {
  month: string
  days: CalendarDay[]
}

export const progressApi = {
  getSummary: () => api.get<ProgressSummary>('/progress/summary'),
  getWeekly: () => api.get<WeeklyData[]>('/progress/weekly'),
  getCalendar: (month: string) => api.get<CalendarData>('/progress/calendar', { params: { month } }),
}

// Daily Plan API
export interface DailyPlan {
  id: string
  date: string
  morning_activity_id: string
  afternoon_activity_id: string
  bedtime_activity_id: string
  morning_status: string
  afternoon_status: string
  bedtime_status: string
  created_at: string
  updated_at: string
}

export const dailyPlanApi = {
  getPlan: () => api.get<DailyPlan>('/daily-plan'),
  updateSlot: (slot: string, status: string) =>
    api.put<DailyPlan>(`/daily-plan/slots/${slot}`, { status }),
  refresh: () => api.post<DailyPlan>('/daily-plan/refresh'),
}

// Demo mode — intercept all API calls with mock responses when backend is unavailable
if (import.meta.env.VITE_DEMO_MODE === 'true') {
  import('./demo-mock').then(({ setupDemoMode }) => setupDemoMode(api))
}
